import { useState } from 'react';
import { Card, Select, DatePicker, Input, Space, Button, Row, Col } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useOrders } from '../hooks/useOrders';
import { OrderTable } from '../components/OrderTable';
import { STATUS_LABELS, CHANNEL_LABELS } from '../utils/constants';
import type { OrderFilters, PaginationParams, OrderStatus, Channel } from '../types';

const { RangePicker } = DatePicker;

export const OrderList: React.FC = () => {
  const [filters, setFilters] = useState<OrderFilters>({});
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    per_page: 20,
    sort_by: 'created_at',
    sort_order: 'desc'
  });

  const { data, isLoading, refetch } = useOrders(filters, pagination);

  const handleStatusChange = (values: OrderStatus[]) => {
    setFilters({ ...filters, status: values });
    setPagination({ ...pagination, page: 1 });
  };

  const handleChannelChange = (values: Channel[]) => {
    setFilters({ ...filters, channel: values });
    setPagination({ ...pagination, page: 1 });
  };

  const handleDateChange = (dates: any) => {
    if (dates) {
      setFilters({
        ...filters,
        date_from: dates[0].format('YYYY-MM-DD'),
        date_to: dates[1].format('YYYY-MM-DD')
      });
    } else {
      const newFilters = { ...filters };
      delete newFilters.date_from;
      delete newFilters.date_to;
      setFilters(newFilters);
    }
    setPagination({ ...pagination, page: 1 });
  };

  const handleSearch = (value: string) => {
    setFilters({ ...filters, search: value || undefined });
    setPagination({ ...pagination, page: 1 });
  };

  const handleReset = () => {
    setFilters({});
    setPagination({
      page: 1,
      per_page: 20,
      sort_by: 'created_at',
      sort_order: 'desc'
    });
  };

  return (
    <div>
      <h1>Замовлення</h1>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              mode="multiple"
              placeholder="Статус"
              style={{ width: '100%' }}
              onChange={handleStatusChange}
              value={filters.status}
              allowClear
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Select
              mode="multiple"
              placeholder="Канал"
              style={{ width: '100%' }}
              onChange={handleChannelChange}
              value={filters.channel}
              allowClear
            >
              {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleDateChange}
              value={
                filters.date_from && filters.date_to
                  ? [dayjs(filters.date_from), dayjs(filters.date_to)]
                  : null
              }
              placeholder={['Дата від', 'Дата до']}
            />
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                Оновити
              </Button>
              <Button onClick={handleReset}>Скинути</Button>
            </Space>
          </Col>

          <Col span={24}>
            <Input.Search
              placeholder="Пошук по імені, телефону, ID замовлення..."
              onSearch={handleSearch}
              enterButton={<SearchOutlined />}
              allowClear
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <OrderTable
          orders={data?.items || []}
          loading={isLoading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.per_page,
            total: data?.total || 0,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, per_page: pageSize });
            }
          }}
        />
      </Card>
    </div>
  );
};
